/** What a month's steps read off the content pack for one archetype, built once per pack (WeakMap) and shared by every seat. */
import {
  DEPOSIT_BOOKS, InvariantViolation, LOAN_BOOKS,
  type ArchetypePack, type BookId, type EngineContext, type EnvSnapshot, type ModelKind, type ModelRefOf, type ProductDef, type ProductId, type Rate, type ScenarioId, type Segment, type VintageId,
} from "@bsim/domain";
import type { CapitalInputs } from "./capital-ratios";
import { IB_DEPOSIT_BOOKS, type IbDepositStart } from "./kpis";
import { CURVE_POINTS, ENV_SCALARS } from "./run/layout";

/** Deposit books that are market products with logit shares (brokered is wholesale, 06.1). */
const MARKET_DEPOSIT_BOOKS: readonly string[] = DEPOSIT_BOOKS.filter((b) => b !== "brokered");
const isLoanBook = (book: BookId): boolean => (LOAN_BOOKS as readonly BookId[]).includes(book);

/** A product's model refs by kind (the first of each kind on `ProductDef.models`). */
export type ProductRefs = { readonly [K in ModelKind]?: ModelRefOf<K> };

export interface ArchetypeInputs {
  readonly pack: ArchetypePack;
  /** Every product; a product's index here is its `productIndex` column and its market-path field. */
  readonly products: readonly ProductDef[];
  readonly productIndex: ReadonlyMap<ProductId, number>;
  readonly segmentByProduct: ReadonlyMap<ProductId, Segment>;
  /** Each product's refs and its 08.1 E1 risk weight, indexed like `products`. */
  readonly refs: readonly ProductRefs[];
  readonly riskWeights: Float64Array;
  readonly depositProducts: readonly ProductDef[];
  readonly loanProducts: readonly ProductDef[];
  readonly startReferenceRates: Readonly<Record<string, Rate>>;
  readonly capital: CapitalInputs;
  /** 05.5 §2: each SimStart loan vintage that carries a fair value → its SimStart FV/cost ratio (resi), kept as its balance moves. */
  readonly loanFairValueRatio: ReadonlyMap<VintageId, number>;
  /** Cumulative IB β's fixed SimStart weights (01.3, `kpis.ts`). */
  readonly ibDepositStart: IbDepositStart;
}

const cache = new WeakMap<ArchetypePack, ArchetypeInputs>();

function refsOf(p: ProductDef): ProductRefs {
  const refs: { [K in ModelKind]?: ModelRefOf<K> } = {};
  for (const m of p.models) if (refs[m.kind] === undefined) (refs as Record<string, ModelRefOf<ModelKind>>)[m.kind] = m;
  return refs;
}

export function archetypeInputs(ctx: EngineContext, archetypeId: string): ArchetypeInputs {
  const pack = ctx.content.archetypes.find((a) => a.id === archetypeId);
  if (pack === undefined) throw new InvariantViolation("PENDING", `archetype ${archetypeId} is not in the content pack`);
  let inputs = cache.get(pack);
  if (inputs === undefined) {
    const nonaccrualRiskWeightMultByProduct: Partial<Record<ProductId, number>> = {};
    for (const p of pack.products) {
      for (const m of p.models) if (m.kind === "creditPd" && m.params.nonaccrualRiskWeightMult !== undefined) nonaccrualRiskWeightMultByProduct[p.id] = m.params.nonaccrualRiskWeightMult;
    }
    const loanFairValueRatio = new Map<VintageId, number>();
    for (const b of LOAN_BOOKS) for (const v of pack.simStart.books[b].vintages) if (v.fairValueUsd !== undefined && v.balanceUsd > 0) loanFairValueRatio.set(v.id, v.fairValueUsd / v.balanceUsd);
    const ibDepositStart = new Map<ProductId, { balanceUsd: number; couponRate: number }>();
    for (const b of IB_DEPOSIT_BOOKS) {
      for (const v of pack.simStart.books[b].vintages) {
        const p = ibDepositStart.get(v.productId) ?? { balanceUsd: 0, couponRate: 0 };
        const balanceUsd = p.balanceUsd + v.balanceUsd;
        ibDepositStart.set(v.productId, { balanceUsd, couponRate: balanceUsd === 0 ? 0 : (p.couponRate * p.balanceUsd + v.couponRate * v.balanceUsd) / balanceUsd });
      }
    }
    inputs = {
      pack, products: pack.products,
      productIndex: new Map(pack.products.map((p, i) => [p.id, i])),
      segmentByProduct: new Map(pack.products.map((p) => [p.id, p.segment])),
      refs: pack.products.map(refsOf),
      riskWeights: Float64Array.from(pack.products, (p) => p.riskWeightRate),
      depositProducts: pack.products.filter((p) => MARKET_DEPOSIT_BOOKS.includes(p.bookId)),
      loanProducts: pack.products.filter((p) => isLoanBook(p.bookId)),
      startReferenceRates: pack.referenceRates,
      capital: { params: ctx.content.params.capital, taxRate: ctx.content.params.taxRate, products: pack.products, nonaccrualRiskWeightMultByProduct },
      loanFairValueRatio, ibDepositStart,
    };
    cache.set(pack, inputs);
  }
  return inputs;
}

/** A product's model ref of a kind, if it has one. */
export const optionalRef = <K extends ModelKind>(product: ProductDef, kind: K): ModelRefOf<K> | undefined =>
  product.models.find((m): m is ModelRefOf<K> => m.kind === kind);

/** A product's model ref of a kind (ProductDef.models), or a content-build defect. */
export function productRef<K extends ModelKind>(product: ProductDef, kind: K): ModelRefOf<K> {
  const ref = optionalRef(product, kind);
  if (ref === undefined) throw new InvariantViolation("PENDING", `product ${product.id} has no ${kind} model`);
  return ref;
}

/** A bank-level model ref (fees, opex, capability). */
export function bankRef<K extends ModelKind>(inputs: ArchetypeInputs, kind: K): ModelRefOf<K> {
  const ref = inputs.pack.bankModels.find((m): m is ModelRefOf<K> => m.kind === kind);
  if (ref === undefined) throw new InvariantViolation("PENDING", `archetype ${inputs.pack.id} has no ${kind} bank model`);
  return ref;
}

/**
 * e₀, the SimStart env (01.1 §3): the scenario's month-0 keyframe, which must carry every env field (content builds it from the
 * SimStart env; a golden's is completed from the pack's by the harness). It is the one anchor of the env path: a field no later
 * keyframe carries holds it, SOFR and CP take their spread over FF from it (01.2 E1). Flags are event-set and start empty. A field
 * missing here is a broken scenario, named at run open, never a NaN at I19.
 */
export function baselineEnvOf(ctx: EngineContext, scenarioId: ScenarioId): EnvSnapshot {
  const scenario = ctx.content.scenarios.find((s) => s.id === scenarioId);
  const month0 = scenario?.keyframes.find((k) => k.month === 0);
  if (month0 === undefined) throw new InvariantViolation("PENDING", `scenario ${scenarioId} has no month-0 keyframe`);
  const env = { flags: {}, ...month0 } as EnvSnapshot;
  const missing = [...ENV_SCALARS.filter((f) => !Number.isFinite(env[f])), ...CURVE_POINTS.filter((c) => !Number.isFinite(env.curveRate?.[c])).map((c) => `curveRate.${c}`)];
  if (missing.length > 0) throw new InvariantViolation("PENDING", `scenario ${scenarioId} month-0 keyframe leaves ${missing.join(", ")} undefined`);
  return env;
}

/** The deposit-flow params every deposit ref of the archetype shares (02.3 pins live on each ref; the pilot's are identical). */
export function depositFlowParams(inputs: ArchetypeInputs): ModelRefOf<"depositFlow">["params"] {
  const first = inputs.depositProducts[0];
  if (first === undefined) throw new InvariantViolation("PENDING", `archetype ${inputs.pack.id} has no deposit products`);
  return productRef(first, "depositFlow").params;
}
